/**
 * The office boy's bell, kept up to date without a page refresh (same pattern as the owners'
 * admin/sync.tsx):
 * - polls /me/notifications every 15 s while the app is visible, and at once when he comes
 *   back to it;
 * - when something new arrives (a "Needs fix" note, a rejection, cash given), the wallets,
 *   Received banner and History reload too, so the screen behind the bell matches it.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import { api } from '../api/index.ts';
import type { SpenderNotification, SpenderNotificationList } from '../api/types.ts';
import { useRoute, WALLET_ALT_HOME } from '../lib/router.ts';
import { signature, withRead } from './notes.ts';
import { useSpender } from './store.tsx';

const POLL_MS = 15_000;

interface NoteSync {
  notes: SpenderNotificationList | null;
  error: boolean;
  refresh: () => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
}

const Ctx = createContext<NoteSync | null>(null);

export function NoteSyncProvider({ children }: { children: ReactNode }) {
  const s = useSpender();
  const [notes, setNotes] = useState<SpenderNotificationList | null>(null);
  const [error, setError] = useState(false);
  const last = useRef('');
  const inFlight = useRef(false);
  const reloadData = useRef(s.refresh);
  reloadData.current = s.refresh;

  const refresh = useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    api
      .notifications()
      .then((n) => {
        setNotes(n);
        setError(false);
        const sig = signature(n);
        // Something changed since the last look: reload what the screens show.
        if (last.current && sig !== last.current) void reloadData.current();
        last.current = sig;
      })
      .catch(() => setError(true))
      .finally(() => {
        inFlight.current = false;
      });
  }, []);

  useEffect(() => {
    refresh();
    const tick = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, POLL_MS);
    const onVisible = () => document.visibilityState === 'visible' && refresh();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    return () => {
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
    };
  }, [refresh]);

  // His own actions (reply, fix, Received) can clear a notification on the server: look again.
  useEffect(() => {
    refresh();
  }, [s.entries, s.cashIns, refresh]);

  const markRead = useCallback(
    (id: string) => {
      setNotes((n) => (n ? withRead(n, id) : n));
      api.markNotificationRead(id).then(refresh).catch(refresh);
    },
    [refresh],
  );

  // Being on a notice's screen reads it (owner's request, 14 Oct 2026): cash given on the home
  // screen (where he confirms it), a "Needs fix" or a rejection on History.
  const route = useRoute();
  const onScreen = (n: SpenderNotification) =>
    n.kind === 'cash_in' ? route.path === '/' || route.path === WALLET_ALT_HOME : route.path === '/history';
  const seen = notes?.items.filter((n) => !n.read && onScreen(n)).map((n) => n.id).join(',') ?? '';
  useEffect(() => {
    if (!seen) return;
    const ids = seen.split(',');
    setNotes((n) => (n ? ids.reduce(withRead, n) : n));
    Promise.all(ids.map((id) => api.markNotificationRead(id)))
      .then(refresh)
      .catch(refresh);
  }, [seen, refresh]);

  const markAllRead = useCallback(() => {
    setNotes((n) => (n ? { unread: 0, items: n.items.map((x) => ({ ...x, read: true })) } : n));
    api.markAllNotificationsRead().then(refresh).catch(refresh);
  }, [refresh]);

  return <Ctx.Provider value={{ notes, error, refresh, markRead, markAllRead }}>{children}</Ctx.Provider>;
}

export function useNoteSync(): NoteSync {
  const v = useContext(Ctx);
  if (!v) throw new Error('useNoteSync outside NoteSyncProvider');
  return v;
}
